import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Download, FileCode2, Globe2, LayoutDashboard, LogOut, Mail, MoreHorizontal } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { LangToggle } from "./kit";
import { IOSHelp, useInstall } from "./Install";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";

export const TABS = [["/app", "nav.dashboard", LayoutDashboard, true], ["/app/messages", "nav.messages", Mail], ["/app/templates", "nav.templates", FileCode2], ["/app/domains", "nav.domains", Globe2]];

function Tab({ to, label, Icon, end, testId, onClick, active }) {
  const cls = (on) => `relative flex min-h-[52px] min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-2xl px-0.5 text-[10px] font-medium transition-colors ${on ? "text-white" : "text-zinc-500 active:text-zinc-300"}`;
  const inner = (on) => <>
    <span className={`grid h-7 w-12 place-items-center rounded-full transition-colors ${on ? "bg-purple-500/20 text-purple-200" : ""}`}><Icon className="h-[18px] w-[18px]" /></span><span className="w-full truncate text-center">{label}</span></>;
  if (onClick) return <button type="button" onClick={onClick} className={cls(active)} data-testid={testId}>{inner(active)}</button>;
  return <NavLink to={to} end={end} className={({ isActive }) => cls(isActive)} data-testid={testId}>{({ isActive }) => inner(isActive)}</NavLink>;
}

export function MobileTabBar({ groups, workspaceSwitcher, onSignOut }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  const inTabs = TABS.some(([to, , , end]) => (end ? pathname === to : pathname.startsWith(to)));
  return (
    <>
      <nav className="pb-safe fixed inset-x-0 bottom-0 z-40 border-t border-white/[0.07] bg-[#050507]/90 px-2 pt-1.5 backdrop-blur-2xl lg:hidden" aria-label="Primary" data-testid="mobile-tab-bar">
        <div className="mx-auto flex max-w-lg items-stretch">
          {TABS.map(([to, key, Icon, end]) => <Tab key={to} to={to} end={end} Icon={Icon} label={t(`tab.${key.split(".")[1]}`)} testId={`mobile-tab-${key.split(".")[1]}`} />)}
          <Tab Icon={MoreHorizontal} label={t("nav.more")} onClick={() => setOpen(true)} active={open || !inTabs} testId="mobile-tab-more" />
        </div>
      </nav>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="pb-safe max-h-[88vh] overflow-y-auto rounded-t-[28px] border-white/10 bg-[#0A090E] px-5 pt-3" data-testid="mobile-more-sheet">
          <SheetTitle className="sr-only">{t("nav.more")}</SheetTitle>
          <div className="mx-auto mb-5 h-1 w-10 rounded-full bg-white/15" />
          <div className="mb-5">{workspaceSwitcher}</div>
          <MoreGrid groups={groups} />
          <MoreFooter onSignOut={onSignOut} />
        </SheetContent>
      </Sheet>
    </>
  );
}

function MoreGrid({ groups }) {
  const { t } = useT();
  const tabPaths = TABS.map(([to]) => to);
  return groups.map(([g, items]) => {
    const rest = items.filter(([to]) => !tabPaths.includes(to));
    if (!rest.length) return null;
    return (
      <div key={g || "root"} className="mb-5">
        {g && <div className="eyebrow mb-2.5 px-1">{t(g)}</div>}
        <div className="grid grid-cols-3 gap-2">{rest.map(([to, key, Icon]) => (
          <NavLink key={to} to={to} data-testid={`mobile-more-${key.split(".")[1]}`}
            className={({ isActive }) => `flex min-h-[78px] flex-col items-start justify-between rounded-2xl border p-3 text-[12.5px] transition-colors ${isActive ? "border-purple-400/30 bg-purple-500/10 text-white" : "border-white/[0.06] bg-white/[0.02] text-zinc-300 active:bg-white/[0.05]"}`}>
            <Icon className="h-[18px] w-[18px] text-purple-300" />{t(key)}
          </NavLink>))}</div>
      </div>
    );
  });
}

function MoreFooter({ onSignOut }) {
  const { t } = useT();
  const { user } = useAuth();
  const { can, install, help, setHelp } = useInstall();
  return (
    <div className="mt-2 space-y-3 border-t border-white/[0.06] pt-5">
      {can && <button onClick={install} className="flex w-full items-center gap-3 rounded-2xl border border-purple-400/25 bg-purple-500/[0.07] p-3.5 text-left" data-testid="mobile-install-app">
        <img src="/icons/icon-192.png" alt="" className="h-10 w-10 rounded-xl" /><div className="flex-1"><div className="text-sm font-medium text-white">{t("pwa.title")}</div><div className="text-xs text-zinc-400">{t("pwa.body")}</div></div><Download className="h-4 w-4 text-purple-200" /></button>}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 truncate text-xs text-zinc-500" data-testid="mobile-user-email">{user?.email}</div><LangToggle prefix="sheet-" />
      </div>
      <button onClick={onSignOut} className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full border border-white/10 text-sm text-zinc-300" data-testid="mobile-signout"><LogOut className="h-4 w-4" />{t("nav.signout")}</button>
      <IOSHelp open={help} onOpenChange={setHelp} />
    </div>
  );
}

// Gives every table cell its column name so tables can stack into cards on phones
export function useTableLabels(ref) {
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    let raf = 0;
    const apply = () => {
      raf = 0;
      root.querySelectorAll("table").forEach((tb) => {
        const heads = [...tb.querySelectorAll("thead th")].map((th) => th.textContent.trim());
        if (!heads.length) return;
        tb.querySelectorAll("tbody tr").forEach((tr) => {
          let i = 0;
          [...tr.children].forEach((td) => {
            const lbl = td.colSpan > 1 ? "" : heads[i] || "";
            if (td.getAttribute("data-label") !== lbl) td.setAttribute("data-label", lbl);
            i += td.colSpan || 1;
          });
        });
      });
    };
    const obs = new MutationObserver(() => { if (!raf) raf = requestAnimationFrame(apply); });
    obs.observe(root, { childList: true, subtree: true });
    apply();
    return () => { obs.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [ref]);
}
